/**
 * Diamond Query Parser — NLP for diamond search queries
 * Parses natural language into structured diamond filter objects.
 * Zero dependencies. Pure client-side.
 */
(function() {
  'use strict';

  // ─── Shape aliases (longest-first for greedy matching) ────────
  var SHAPE_ALIASES = [
    { keys: ['round brilliant', 'round'], canonical: 'Round' },
    { keys: ['square cushion', 'sq cushion'], canonical: 'Square Cushion' },
    { keys: ['long cushion', 'elongated cushion'], canonical: 'Long Cushion' },
    { keys: ['cushion'], canonical: 'Square Cushion' },
    { keys: ['emerald cut', 'emerald'], canonical: 'Emerald' },
    { keys: ['princess cut', 'princess'], canonical: 'Princess' },
    { keys: ['radiant cut', 'radiant'], canonical: 'Radiant' },
    { keys: ['marquise', 'marquis'], canonical: 'Marquise' },
    { keys: ['asscher'], canonical: 'Asscher' },
    { keys: ['heart'], canonical: 'Heart' },
    { keys: ['oval'], canonical: 'Oval' },
    { keys: ['pear'], canonical: 'Pear' }
  ];

  // Minimum chars for partial matching (avoids "a" matching "asscher")
  var MIN_PARTIAL_LEN = 3;

  // ─── Cut grade mapping ────────────────────────────────────────
  var CUT_MAP = {
    'excellent': 'EX', 'ex': 'EX',
    'ideal': 'ID', 'id': 'ID',
    'very good': 'VG', 'verygood': 'VG', 'vg': 'VG',
    'good': 'GD', 'gd': 'GD'
  };

  // ─── Diamond context words (used to validate ambiguous matches)
  var CONTEXT_WORDS = /\b(diamond|diamonds|stone|stones|loose|carat|ct|brilliant|solitaire)\b/i;

  // ─── Parse function ───────────────────────────────────────────
  function parse(query) {
    if (!query || typeof query !== 'string') {
      return { shape: null, carat: null, color: null, clarity: null, cut: null, lab: null, isDiamondQuery: false };
    }

    var q = query.toLowerCase().trim();
    var result = { shape: null, carat: null, color: null, clarity: null, cut: null, lab: null, isDiamondQuery: false };
    var foundTerms = 0;

    // ── Shape detection ──
    var shapeMatch = matchShape(q);
    if (shapeMatch) {
      result.shape = shapeMatch;
      foundTerms++;
    }

    // ── Carat detection ──
    var caratMatch = q.match(/(\d+\.?\d*)\s*(?:ct|carat|carats?|-carat)/i);
    if (!caratMatch) {
      // Also try: "2 ct" with space
      caratMatch = q.match(/(\d+\.?\d*)\s+ct\b/i);
    }
    if (caratMatch) {
      var val = parseFloat(caratMatch[1]);
      if (val >= 0.1 && val <= 30) {
        result.carat = val;
        foundTerms++;
      }
    }

    // ── Clarity detection (unambiguous acronyms) ──
    var clarityMatch = q.match(/\b(FL|IF|VVS1|VVS2|VS1|VS2|SI1|SI2)\b/i);
    if (clarityMatch) {
      result.clarity = clarityMatch[1].toUpperCase();
      foundTerms++;
    }

    // ── Lab detection ──
    var labMatch = q.match(/\b(GIA|IGI)\b/i);
    if (labMatch) {
      result.lab = labMatch[1].toUpperCase();
      foundTerms++;
    }

    // ── Cut detection ──
    var cutResult = matchCut(q);
    if (cutResult) {
      result.cut = cutResult;
      foundTerms++;
    }

    // ── Color detection (careful — single letters are ambiguous) ──
    var colorResult = matchColor(q, foundTerms > 0);
    if (colorResult) {
      result.color = colorResult;
      foundTerms++;
    }

    // ── isDiamondQuery: at least one filter found, OR query contains "diamond(s)" ──
    result.isDiamondQuery = foundTerms > 0 || CONTEXT_WORDS.test(q);

    return result;
  }

  // ─── Shape matching (exact + partial) ─────────────────────────
  function matchShape(q) {
    // Exact match first (longest alias first)
    for (var i = 0; i < SHAPE_ALIASES.length; i++) {
      var entry = SHAPE_ALIASES[i];
      for (var j = 0; j < entry.keys.length; j++) {
        var re = new RegExp('\\b' + escapeRegex(entry.keys[j]) + '\\b', 'i');
        if (re.test(q)) return entry.canonical;
      }
    }

    // Partial match (startsWith, min 3 chars)
    var words = q.split(/\s+/);
    for (var w = 0; w < words.length; w++) {
      var word = words[w];
      if (word.length < MIN_PARTIAL_LEN) continue;
      for (var i2 = 0; i2 < SHAPE_ALIASES.length; i2++) {
        var entry2 = SHAPE_ALIASES[i2];
        for (var j2 = 0; j2 < entry2.keys.length; j2++) {
          if (entry2.keys[j2].indexOf(word) === 0 && word.length >= MIN_PARTIAL_LEN) {
            return entry2.canonical;
          }
        }
      }
    }

    return null;
  }

  // ─── Cut matching ─────────────────────────────────────────────
  function matchCut(q) {
    // "very good" (two words) first
    if (/\bvery\s*good\s*(?:cut)?\b/i.test(q)) return 'VG';
    // "excellent cut", "excellent"
    if (/\bexcellent\s*(?:cut)?\b/i.test(q)) return 'EX';
    // "ideal cut", "ideal"
    if (/\bideal\s*(?:cut)?\b/i.test(q)) return 'ID';
    // "good cut" (require "cut" — "good" alone is too ambiguous)
    if (/\bgood\s+cut\b/i.test(q)) return 'GD';
    // Short codes with "cut" context
    if (/\b(EX|ID|VG|GD)\s*cut\b/i.test(q)) {
      var m = q.match(/\b(EX|ID|VG|GD)\s*cut\b/i);
      return CUT_MAP[m[1].toLowerCase()] || null;
    }
    return null;
  }

  // ─── Color matching ───────────────────────────────────────────
  function matchColor(q, hasOtherDiamondTerms) {
    // Explicit: "D color", "color D", "D colour"
    var explicit = q.match(/\b([D-Kd-k])\s*colou?r\b/i) || q.match(/\bcolou?r\s+([D-Kd-k])\b/i);
    if (explicit) return explicit[1].toUpperCase();

    // Only match bare single letters if other diamond terms are present
    if (hasOtherDiamondTerms || CONTEXT_WORDS.test(q)) {
      // Match standalone color grade letter (word boundary on both sides)
      // Exclude common false positives: "a", "i" (already excluded by D-K range)
      var bare = q.match(/(?:^|\s)([D-Kd-k])(?:\s|$)/);
      if (bare) {
        var letter = bare[1].toUpperCase();
        // Don't match if it's part of a shape name we already detected
        // "E" could be start of "emerald" — but emerald would be caught by shape parser first
        // Just return the color
        return letter;
      }
    }

    return null;
  }

  // ─── Build URL from parsed filters ────────────────────────────
  function buildURL(filters) {
    var params = [];

    if (filters.shape) params.push('shape=' + encodeURIComponent(filters.shape));

    if (filters.carat) {
      // Whole number: 2 → 2.00-2.99. Decimal: 1.5 → 1.50-1.59
      var c = filters.carat;
      var isWhole = c === Math.floor(c);
      if (isWhole) {
        params.push('carat_min=' + c.toFixed(2));
        params.push('carat_max=' + (c + 0.99).toFixed(2));
      } else {
        params.push('carat_min=' + c.toFixed(2));
        params.push('carat_max=' + (c + 0.09).toFixed(2));
      }
    }

    if (filters.color) {
      params.push('color_min=' + filters.color);
      params.push('color_max=' + filters.color);
    }

    if (filters.clarity) {
      params.push('clarity_min=' + filters.clarity);
      params.push('clarity_max=' + filters.clarity);
    }

    if (filters.cut) {
      params.push('cut_min=' + filters.cut);
      params.push('cut_max=' + filters.cut);
    }

    if (filters.lab) {
      params.push('lab=' + filters.lab);
    }

    return '/pages/diamond-search' + (params.length ? '?' + params.join('&') : '');
  }

  // ─── Build human-readable label ───────────────────────────────
  function buildLabel(filters) {
    var parts = [];

    if (filters.carat) parts.push(formatCarat(filters.carat));
    if (filters.shape) parts.push(filters.shape);
    if (filters.color) parts.push(filters.color);
    if (filters.clarity) parts.push(filters.clarity);
    if (filters.cut) parts.push(cutLabel(filters.cut));
    if (filters.lab) parts.push(filters.lab);

    if (parts.length === 0) return 'All Diamonds';
    return parts.join(' ') + ' Diamonds';
  }

  function formatCarat(c) {
    if (c === Math.floor(c)) return c + 'ct';
    return c.toFixed(2) + 'ct';
  }

  function cutLabel(code) {
    var map = { EX: 'Excellent', ID: 'Ideal', VG: 'Very Good', GD: 'Good' };
    return map[code] || code;
  }

  function escapeRegex(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // ─── Generate suggestion objects ──────────────────────────────
  function buildSuggestions(filters) {
    if (!filters.isDiamondQuery) return [];

    var suggestions = [];
    var filterCount = 0;
    if (filters.shape) filterCount++;
    if (filters.carat) filterCount++;
    if (filters.color) filterCount++;
    if (filters.clarity) filterCount++;
    if (filters.cut) filterCount++;
    if (filters.lab) filterCount++;

    // Primary: full match with all filters
    if (filterCount > 0) {
      suggestions.push({
        label: buildLabel(filters),
        url: buildURL(filters)
      });
    }

    // Secondary: shape-only (if query had shape + other filters)
    if (filterCount > 1 && filters.shape) {
      var shapeOnly = { shape: filters.shape, carat: null, color: null, clarity: null, cut: null, lab: null, isDiamondQuery: true };
      suggestions.push({
        label: 'All ' + filters.shape + ' Diamonds',
        url: buildURL(shapeOnly)
      });
    }

    // Tertiary: carat-only (if query had carat + other filters)
    if (filterCount > 1 && filters.carat) {
      var caratOnly = { shape: null, carat: filters.carat, color: null, clarity: null, cut: null, lab: null, isDiamondQuery: true };
      suggestions.push({
        label: 'All ' + formatCarat(filters.carat) + ' Diamonds',
        url: buildURL(caratOnly)
      });
    }

    // If just "diamonds" typed with no specific filters
    if (filterCount === 0 && filters.isDiamondQuery) {
      suggestions.push({
        label: 'Browse All Diamonds',
        url: '/pages/diamond-search'
      });
    }

    return suggestions.slice(0, 3);
  }

  // ─── Expose API ───────────────────────────────────────────────
  window.DiamondQueryParser = {
    parse: parse,
    buildURL: buildURL,
    buildLabel: buildLabel,
    buildSuggestions: buildSuggestions
  };
})();